<template>
  <section class="view">
    <ToastVoucher :toast-title="checkin ? checkin.pica_service.voucher_cta_title : null"
                  :toast-text="checkin ? checkin.pica_service.voucher_cta_text : null"/>
    <ViewLoader v-if="loading"/>
    <template v-else>
      <ModalVoucher :checkinId="checkin.checkin_id" @voucher-redeemed="getCheckinDetail(checkin.picacode)"/>
      <ViewHeader>

        <template #content>
          <div class="checkin-detail-header">
            <div class="container">
              <div class="row-small-gutters">
                <div class="col-12 col-lg">
                  <div class="event-summary">
                    <div class="d-lg-flex justify-content-between">
                      <div
                          class="d-flex justify-content-between justify-content-lg-start align-items-start w-100 w-lg-auto">
                        <div class="d-flex align-items-center w-100 w-lg-auto">
                          <div class="event-summary__logo" v-if="checkin.pica_service.event_cover">
                            <img :src="checkin.pica_service.event_cover" alt="Event logo">
                          </div>
                          <div class="w-100 w-lg-auto">
                            <div class="d-flex justify-content-between align-items-start">
                              <div>
                                <h1 class="event-summary__name">{{ eventName }}</h1>
                                <div class="d-flex align-items-baseline">
                                  <div>
                                    <div class="event-status__icon"
                                         v-if="!checkin.pica_service.is_tagging_completed"></div>
                                  </div>
                                  <h6 class="event-summary__subname">{{ checkin.pica_service.event_photo_service }}</h6>
                                </div>
                              </div>
                              <!-- CONTEXT MENU -->
                              <checkin-context-menu
                                  :checkin="checkin"
                                  @remove-album="$bvModal.show('bv-modal-remove-checkin')"
                                  @anonymize-album="$bvModal.show('modal-anonymize-checkin')"
                                  @show-qr-picacode="$bvModal.show('bv-modal-show-qr-picacode')"
                                  @download-album="downloadAlbum"/>
                            </div>

                          </div>
                        </div>
                      </div>
                      <div class="text-right">
                        <a class="btn--pricelist mt-lg-0 mt-4" href="#"
                           v-if="showPriceList && priceListPosition.includes('top')"
                           @click.prevent="$bvModal.show('bv-modal-price-list')">
                          {{ $t('pages.checkins_detail.label_price_list') }}
                          <Icon name="coin" width="20"/>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div v-if="checkin.max_faces_count > 1 && checkin.checkin_faces.length > 0">
                    <div class="d-flex justify-content-start flex-wrap my-4">
                      <CheckinFaceList @click="openFaceRecognitionModal()" :checkin-faces="checkin.checkin_faces"
                                       :max-faces-count="checkin.max_faces_count"
                                       :disabled="checkin.checkin_faces.length === checkin.max_faces_count"/>
                    </div>
                  </div>

                  <CheckinDetailTiming class="mt-4" :timing-data="checkin.timing" v-if="checkin.timing"/>

                  <template v-if="hdAvailableTo">
                    <div class="checkin__last-call mt-4 d-flex align-items-center justify-content-between">
                      <div>
                        <Icon name="infoCircle" width="20" class="mr-2"/>
                        {{ messageAvailableTo }}
                      </div>
                      <div>
                        <b-button size="sm" class="" variant="outline" @click="$bvModal.show('modal-last-chance-hd')">
                          {{ $t('pages.checkins.last_call_btn2') }}
                        </b-button>
                      </div>
                    </div>
                  </template>
                </div>
                <template v-if="currentTab === BUYABLE && customBannersOnTop.length > 0">
                  <div class="d-none d-xxl-block col-1"></div>
                  <!-- CUSTOM PACKAGE -->
                  <div class="col col-lg-3 col-xxl-2 mt-3 mt-lg-0" v-for="(banner, index) in customBannersOnTop"
                       :key="`top-banner-${index}`">
                    <CheckinDetailPackageBanner
                        position="top"
                        :title="banner.title"
                        :subtitle="banner.subtitle"
                        :price="banner.price"
                        :discount="banner.discount"
                        :price-original="banner.price_original"
                        :is-already-added="banner.is_added_in_cart"
                        :btn-full-witdh-on-desktop="false"
                        theme-clr-from="#38377B"
                        theme-clr-to="#C849AF"
                        @clicked-buy-now="activateCustomPackage(banner.endpoint_url)"/>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>
      </ViewHeader>

      <div class="view-body checkin-detail-body">
        <div class="container">
          <template v-if="!showUploadingMessage">
              <!-- CTA: NONE -->
              <template v-if="checkin.user_cta === userCTA.NONE && !hasBoughtOrHasBuyable">
                <template v-if="!eventClosed">
                   <CtaSupport :event-name="eventName" :pica-code="picaCode" v-if="eventIsStarted"/>
                   <CtaIncomingEvent :event-name="eventName" :pica-code="picaCode" v-else/>
                </template>
                <CtaSupport :event-name="eventName" :pica-code="picaCode" v-else/>
              </template>

              <!-- CTA: SUPPORT -->
              <CtaSupport :event-name="eventName"
                          :pica-code="picaCode"
                          v-else-if="checkin.user_cta === userCTA.SUPPORT" />

              <!-- CTA: FACE_CHECKIN -->
              <CtaFaceCheckin :event-name="eventName"
                              :pica-code="picaCode"
                              v-else-if="checkin.user_cta === userCTA.FACE_CHECKIN"
                              @click="openFaceRecognitionModal(true)" />
          </template>
          <CtaUploading :event-name="eventName" :pica-code="picaCode" v-else/>
        </div>
        <div class="container">
          <div class="row" v-if="showNavPills">
            <div class="w-auto mx-auto">
              <nav class="nav nav-pills flex-row">
                <span class="flex-fill text-center nav-link"
                      :class="{ active: currentTab === BUYABLE }"
                      @click="currentTab = BUYABLE">
                  {{ $t('pages.checkins_detail.not_purchased_yet') }}
                </span>
                <span class="flex-fill text-center nav-link" :class="{ active: currentTab === BOUGHT }"
                      @click="currentTab = BOUGHT">
                  {{ $t('pages.checkins_detail.purchased') }}
                  <span class="badge badge-pill" v-if="boughtCount"> {{ boughtCount }}</span>
                </span>
              </nav>
            </div>
          </div>
        </div>

        <CheckinDetailPhotosList
            v-if="hasBoughtOrHasBuyable"
            @photo-package-activated="activatePackage('photopackage')"
            :checkin="checkin"
            :current-tab="currentTab"
            ref="checkinDetailPhotosList"
        />

        <div class="container">
          <div class="checkin-detail-header d-flex justify-content-end">
            <a class="view-btn btn--pricelist" href="" v-if="showPriceList && priceListPosition.includes('bottom')"
               @click.prevent="$bvModal.show('bv-modal-price-list')">
              {{ $t('pages.checkins_detail.label_price_list') }}
              <Icon name="coin" width="20"/>
            </a>
          </div>
          <div class="row-small-gutters">
            <!-- CUSTOM PACKAGE -->
            <template v-if="currentTab === BUYABLE && customBannersOnBottom.length > 0">
              <div class="col mt-3 mt-lg-0" v-for="(banner, index) in customBannersOnBottom"
                   :key="`bottom-banner-${index}`">
                <CheckinDetailPackageBanner
                    position="top"
                    :title="banner.title"
                    :subtitle="banner.subtitle"
                    :price="banner.price"
                    :discount="banner.discount"
                    :price-original="banner.price_original"
                    :is-already-added="banner.is_added_in_cart"
                    :btn-full-witdh-on-desktop="false"
                    theme-clr-from="#38377B"
                    theme-clr-to="#C849AF"
                    @clicked-buy-now="activateCustomPackage(banner.endpoint_url)"/>
              </div>
            </template>
          </div>
        </div>

        <!-- BUTTON BANNERS -->
        <div class="button-banners-wrap">
          <div class="button-banners container-fluid"
               v-if="currentTab === BUYABLE && (showDefaultBanners || customBannersOnTop.length > 0)">
            <div class="row">
              <div class="col-12">
                <!-- NEW PACKAGE BUTTONS -->
                <div class="d-md-flex packages-btn justify-content-md-center">
                  <div class="mx-3" v-if="showPackagePhotosButton">
                    <checkin-detail-package-button
                        :title="$t('pages.checkins_detail.package_photos_btn_buy_all')"
                        :subtitle="$t('pages.checkins_detail.package_banner_subtitle')"
                        :extra-class="'btn-buy--package-photos'"
                        :price="priceForAllPhotos"
                        :price-original="priceOriginalForAllPhotos"
                        :is-already-added="isPhotoPackageAlreadyInCart && !isVideoPackageAlreadyInCart"
                        @clicked-buy-now="activatePackage('photopackage')"/>
                  </div>
                  <div class="mx-3" v-if="showPackagePhotosVideosButton">
                    <checkin-detail-package-button
                        :title="$t('pages.checkins_detail.package_photos_videos_btn_buy_all')"
                        :subtitle="$t('pages.checkins_detail.package_banner_subtitle')"
                        :extra-class="'btn-buy--package-photos-videos'"
                        :price="priceForAllPhotosAndVideos"
                        :price-original="priceOriginalForAllPhotosAndVideos"
                        :is-already-added="isVideoPackageAlreadyInCart"
                        @clicked-buy-now="activatePackage('video')"/>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div class="mt-2">
          <StaticBanner :pica-service-id="picaServiceId" :banner="eventBanner"/>
          <WelcomePopup :pica-service-id="picaServiceId" :active="eventPopup.welcome.active === true"
                        :title="eventPopup.welcome.title" :text="eventPopup.welcome.text"/>
          <FixedPopup :pica-service-id="picaServiceId" :active="eventPopup.fixed.active === true"
                      :title="eventPopup.fixed.title" :text="eventPopup.fixed.text"/>

        </div>

        <ShareWithFriends :data="shareWithFriends" :event-name="eventName" v-if="shareWithFriends.enabled"/>

        <AlaskaCTA
            :url="'gohski.es/3RXKwOC'"
            :facebook="'https://pica.musvc2.net/e/tr?q=6%3dIRCaPY%26w%3dT%266%3dRIaQ%26F%3dCWLRCY%26C%3d95M8J_3tkq_Dd_0rds_Jg_3tkq_CiEN8.9s4p57Fv.67D_3tkq_CiA9lKwI_3tkq_CiA9lKwI.1A8_Ibvh_TQN_0rds_KerHzJvB.wJ_3tkq_CiKsinEpN%267%3d5L4OuS.y8B%2604%3dTKVGV%26BJ%3dCcJTBcJaBVLSFY%26z%3d6m4OTGWL5EWL7n5KWJYQYHXw7q74s2lx2o9M7p9vTpVsaE4KYFUQ7HTvVmbI5I6P3IWN'"
            :telegram="'https://pica.musvc2.net/e/tr?q=6%3dMS6aTZ%26p%3dT%260%3dSBaU%26G%3d6WPS6Y%26G%3d0xMBK_vtor_7d_DsWs_Nh_vtor_6iF.Ei_KmuT_UbKl4D7_vtor_6iGJp_KmuT_VbMvE_DsWs_OfkH4KoB.1K_vtor_6iOtbnIqG_KmuT_URLiQF_JUvl_UH_KmuT_iV_JUvl_aJ_KmuT_bN_JUvl_aG_KmuT_UQTDzWuL_KmuT_UQTDOE_JUvl_T9UVqVh_DsWs_NW6c_DsWs_Se_vtor_6XNbEEwKo4_DsWs_NW6coC3_KmuT_UQTDv4GxL_DsWs_Oc_vtor_6XNbPHAC_vtor4w3e_6XNbeM_DsWs_NW6cKGyK_DsWs_NW6cB0sMAK_vtor_6XNbeM_DsWs_NW6cF0i_KmuT_UQTDkGKoB1K_vtor_6XNbk497!_vtor_6XNb_vtor_6XNb_vtor_Jc_DsWs_Uh_vtor_CT_DsWs_TY%26h%3dFGKz6N.FiM%265K%3d5VQX7%26ME%3dTDUOSDUVS7WNW0%26A%3dMSCTzXjZV3DXRTB91a84M8BTx3ibO6eZz8AaS86XPWfby6gW1YDZV30UNZ9Tw8A5'"
            :whatsapp="'https://pica.musvc2.net/e/tr?q=9%3dJS6dQZ%26p%3dW%267%3dSBdR%26G%3d6ZMS6b%26D%3d0xP9K_vwlr_7g_AsWv_Kh_vwlr_6ltHm.S13xOtHt.98E_vwlr_6lB7r0_AsWv_LhxAGL_vwlr_7j_AsWv_Yb_vwlr_Cl_AsWv_RT_vwlr_Ci_AsWv_KW6fpcWn_AsWv_KW6fEK_vwlr_6aKbSxX_JUyi_T9XS_JUyi_YG_NjuT_XNTDg53wGt_JUyi_T9XSuoU_AsWv_KW6fl0sPB_JUyi_UE_NjuT_XNTDr8Go_NjuT_XNTD7C_JUyi_T9XSQsQA_JUyi_T9XSHlKCGw_NjuT_7t3eXNTD7C_JUyi_T9XSLlA_AsWv_KW6faMwG27w_NjuT_XNTDCtEi!_NjuT_XNTD_NjuT_XNTD_NjuT_lS_JUyi_aJ_NjuT_eJ_JUyi_ZA_NjuT_XNbeC80wG2.7w_NjuT_XYUV4dOSi%26w%3dEyOE56.JxL%26mO%3dJU9bL%26Lw%3dXST7WSTDWLV6aO%260%3dCXK480QZ8bQ788y70bx4AYRSBWR49WtV9WNV7cJ8h8x6jZtTjeMU6aRX50M89fS3'"
            :email="'mailto:?subject=%F0%9F%92%9C%20WASH%20vs%20ORE%20%7C%20Alaska%20Sky%20Shots%3A%20Look%20at%20your%20photos%20at%20the%20Huskies%20game%21&body=Look%20at%20your%20photos%3A%20gohski.es%2F3RXKwOC'"
            v-if="checkin && picaServiceId === 2295"/>

        <AlaskaCTA
            :url="'https://bit.ly/40TBQvc'"
            :facebook="'https://www.facebook.com/sharer/sharer.php?u=https://bit.ly/40TBQvc'"
            :telegram="`https://t.me/share/url?url=https://bit.ly/40TBQvc&text=📸[${eventName}]: Look at your photos! 👇`"
            :whatsapp="`https://api.whatsapp.com/send?text=📸[${eventName}]: Look at your photos! 👇 https://bit.ly/40TBQvc`"
            :email="`mailto:?subject=📸[${eventName}]: Look at your photos!&body=Look at your photos: https://bit.ly/40TBQvc`"
            v-if="checkin && picaServiceId === 2394"/>
      </div>


      <ModalRemoveCheckin :checkin-id="checkin.checkin_id" :picacode="checkin.picacode"/>

      <ModalShowPicacode :picacode="checkin.picacode"/>
      <ModalShowQrPicacode :picacode="checkin.picacode"/>
      <ModalAnonymizeCheckin :checkin-id="checkin.checkin_id"
                             :checkin-anonymize-status="checkin.anonymize_status"
                             :checkin-event-name="eventName"
                             :checkin-anonymize-mode="checkin.anonymize_mode"
                             @anonymize-call-back="onAnonymizeCallBack"/>
      <ModalPriceList v-if="showPriceList" :price-list-items="checkin.price_list.items"
                      :external-price-list-url="checkin.price_list.price_list_file"
                      :currency="checkin.pica_service.currency"
                      :show-video-package-price="hasBuyableVideos"
                      :discount-description="checkin.price_list.discount_description"
                      :event-name="eventName"/>
      <ModalLastChanceHD :date="hdAvailableTo" v-if="hdAvailableTo"/>
      <ModalCustomCheckinDetail :param="modalCustom"/>


      <b-modal :id="'modal-custom-video'" modal-class="modal-video-custom " centered size="md" hide-header hide-footer>
        <div style="position: relative">
          <div class="text">
            <h6>{{ $t('pages.custom_video.title_modal') }}</h6>
            <p v-html="$t('pages.custom_video.message_modal')"></p>
          </div>
          <checkin-detail-package-button
              :title="$t('pages.checkins_detail.package_photos_videos_btn_buy_all')"
              :extra-class="'btn-primary'"
              :price="priceForAllPhotosAndVideos"
              :price-original="priceOriginalForAllPhotosAndVideos"
              :is-already-added="isVideoPackageAlreadyInCart"
              @clicked-buy-now="activatePackage('video')"/>
        </div>
        <b-button variant="link" size="sm" block @click="$bvModal.hide('modal-custom-video')">
          {{ $t('modal_pre_checkin_face_recognition.btn_back') }}
        </b-button>
      </b-modal>


    </template>
  </section>

</template>


<script>
import EventService from "@/services/Event.js"
import ViewLoader from "@/components/ViewLoader"
import ViewHeader from '@/components/ViewHeader'
import CheckinDetailTiming from "@/components/CheckinDetailTiming"
import CheckinDetailPhotosList from "@/components/CheckinDetailPhotosList"
import CheckinDetailPackageBanner from "@/components/CheckinDetailPackageBanner"
import CheckinFaceList from "@/components/CheckinFaceList"
import ModalRemoveCheckin from "@/components/ModalRemoveCheckin"
import ModalShowPicacode from "@/components/ModalShowPicacode"
import ModalAnonymizeCheckin from "@/components/ModalAnonymizeCheckin"
import ModalPriceList from "@/components/ModalPriceList"
import ModalVoucher from "@/components/ModalVoucher"
import ToastVoucher from "@/components/ToastVoucher"
import {getContactUrl} from "@/utils";
import {mapActions, mapGetters, mapMutations} from "vuex";
import CheckinDetailPackageButton from "@/components/CheckinDetailPackageButton";
import CheckinContextMenu from "@/components/CheckinContextMenu.vue";
import ModalShowQrPicacode from "@/components/ModalShowQrPicacode.vue";
import Gallery from "@/domain/metrics/gallery";
import StaticBanner from "@/components/customEvents/StaticBanner.vue";
import ModalCustomCheckinDetail from "@/components/ModalCustomCheckinDetail.vue";
import {observeElement} from "@/utils/media";
import AlaskaCTA from "@/components/customEvents/AlaskaCTA.vue";
import {userCTA} from "@/consts";
import ShareWithFriends from "@/domain/checkinDetail/ShareWithFriends.vue";
import ModalLastChanceHD from "@/components/ModalLastChanceHD.vue";
import WelcomePopup from "@/components/customEvents/WelcomePopup.vue";
import FixedPopup from "@/components/customEvents/FixedPopup.vue";
import CtaSupport from "@/domain/checkinDetail/cta/CtaSupport.vue";
import CtaFaceCheckin from "@/domain/checkinDetail/cta/CtaFaceCheckin.vue";
import CtaUploading from "@/domain/checkinDetail/cta/CtaUploading.vue";
import CtaIncomingEvent from "@/domain/checkinDetail/cta/CtaIncomingEvent.vue";

const BUYABLE = 'Buyable'
const BOUGHT = 'Bought'

export default {
  components: {
    CtaIncomingEvent,
    CtaUploading,
    CtaFaceCheckin,
    CtaSupport,
    FixedPopup,
    WelcomePopup,
    ModalLastChanceHD,
    ShareWithFriends,
    AlaskaCTA,
    ModalCustomCheckinDetail,
    StaticBanner,
    CheckinContextMenu,
    CheckinDetailPackageButton,
    ViewLoader,
    ViewHeader,
    CheckinDetailTiming,
    CheckinDetailPhotosList,
    CheckinDetailPackageBanner,
    CheckinFaceList,
    ModalRemoveCheckin,
    ModalShowPicacode,
    ModalShowQrPicacode,
    ModalAnonymizeCheckin,
    ModalPriceList,
    ModalVoucher,
    ToastVoucher
  },

  data() {
    return {
      BUYABLE: BUYABLE,
      BOUGHT: BOUGHT,
      loading: true,
      priceForAllPhotos: null,
      priceForAllPhotosAndVideos: null,
      priceOriginalForAllPhotos: null,
      priceOriginalForAllPhotosAndVideos: null,
      currentTab: null,
      showUploadingMessage: false,
      timerStarted: false,
      alaskaBanner: null,
      modalCustom: {
        title: null,
        text: null,
        btnOk: null,
        ctas: [],
        config: {scroll: true, textClass: 'text-secondary', textCenter: false}
      }
    }
  },

  async mounted() {

    // If appConfig was already loaded => load checkin data
    if (this.getAppConfigReady) {
      await this.loadCheckinDetailData()
    }
  },

  beforeDestroy() {
    this.exitCheckin()
    this.unsetEventPopup()

    if (this.timerStarted) Gallery.exitAlbum()

    this.UNSET_PICASERVICE()

    if (this.checkinEventsHandler) {
      this.$store.dispatch('websocket/unsubscribeOnMessageHandler', this.checkinEventsHandler)
    }
  },

  watch: {
    // If appConfig was NOT already loaded  => watch appConfigReady
    getAppConfigReady() {
      let ready = this.$store.getters['appConfig/getAppConfigReady']
      if (ready) {
        this.loadCheckinDetailData()
      }
    }
  },

  computed: {
    userCTA() {
      return userCTA
    },
    ...mapGetters(['getCustomModalCheckinDetailShown']),
    ...mapGetters('auth', [
      'currentUser'
    ]),
    ...mapGetters('checkinDetail', [
      'checkin',
      'eventBanner',
      'eventPopup',
      'picaServiceId',
      'isPromo',
      'eventClosed',
      'hasBuyablePhotos',
      'hasBuyableVideos',
      'priceList',
      'eventName',
      'picaCode',
      'hasBought',
      'canBuy',
      'boughtCount',
      'shareWithFriends',
      'useGetPicaForYourEvent'
    ]),
    ...mapGetters('appConfig', [
      'getAppConfigReady'
    ]),
    hasBoughtOrHasBuyable() {
      return this.hasBuyablePhotos || this.hasBuyableVideos || this.hasBought
    },
    timeoutObserver() {
      return (this.hasBuyablePhotos || this.hasBuyableVideos) ? 2000 : 10
    },
    showPriceList() {
      return !this.isPromo && this.getPriceByQty(-1, false, false) > 0
    },
    contactUrl() {
      return getContactUrl(`${this.eventName} | ${this.picaCode}`)
    },
    contactMessage() {
      return this.$t('pages.checkins.contact_support_with_problem', {
        contactUrl: this.contactUrl
      })
    },
    priceListPosition() {
      return this.priceList && this.priceList.position ? this.checkin.price_list.position : 'top'
    },
    showDefaultBanners() {
      return this.priceList ? this.priceList.show_default_banners : false
    },
    customBannersOnTop() {
      return this.priceList ? this.priceList.custom_banners.filter(i => i.position.split('+').includes('top')) : []
    },
    customBannersOnBottom() {
      return this.priceList ? this.priceList.custom_banners.filter(i => i.position.split('+').includes('bottom')) : []
    },
    isPhotoPackageAlreadyInCart() {
      const digitalItem = this.$store.getters['cart/getDigitalItemByPicacode'](this.checkin.picacode)
      return digitalItem && digitalItem.order_type ? digitalItem.order_type.includes('photopackage') : false
    },
    isVideoPackageAlreadyInCart() {
      const digitalItem = this.$store.getters['cart/getDigitalItemByPicacode'](this.checkin.picacode)
      return digitalItem && digitalItem.order_type ? digitalItem.order_type.includes('video') : false
    },
    isPresaleAvailable(){
      return !this.eventClosed && !this.hasBuyablePhotos
    },
    isPhotoPackageBannerEnabled() {
      return (this.hasBuyablePhotos || this.isPresaleAvailable) && this.getPriceByQty(-1, false, false) > 0
    },
    isVideoPackageBannerEnabled() {
      return this.hasBuyableVideos && this.checkin.pica_service.allows_upload_video && this.getPriceByQty(-2, false, false) > 0
    },
    showNavPills() {
      if (this.isPromo) return false
      return this.canBuy && this.hasBought && (this.hasBuyablePhotos || this.hasBuyableVideos)
    },
    digitalItemId() {
      const digitalItem = this.$store.getters['cart/getDigitalItemByPicacode'](this.checkin.picacode)
      return digitalItem ? digitalItem.id : null
    },
    showPackagePhotosButton() {
      return this.showDefaultBanners && this.isPhotoPackageBannerEnabled && !(this.isPhotoPackageAlreadyInCart && !this.isVideoPackageAlreadyInCart)
    },
    showPackagePhotosVideosButton() {
      return this.showDefaultBanners && this.isVideoPackageBannerEnabled && !this.isVideoPackageAlreadyInCart
    },
    isTwelveHoursOverEventEnd() {
      const eventEnd = new Date(this.checkin.pica_service.event_end_at)
      let twelveHoursEventEnd = new Date(eventEnd)
      twelveHoursEventEnd.setHours(twelveHoursEventEnd.getHours() + 12)
      return new Date() > twelveHoursEventEnd
    },
    eventIsStarted() {
      return new Date() > new Date(this.checkin.pica_service.event_start_at)
    },
    hdAvailableTo() {
      if (this.checkin.pica_service.hd_available_to) {
        return this.$options.filters.formatDate(this.checkin.pica_service.hd_available_to)
      } else {
        return null
      }
    },
    messageAvailableTo() {
      return this.$t('pages.checkins.last_call_message', {date: this.hdAvailableTo})
    },
  },

  methods: {
    ...mapMutations('event', ['SET_PICASERVICE', 'UNSET_PICASERVICE']),
    ...mapActions('checkin', [
      'openAddSelfieStep'
    ]),
    ...mapActions('checkinDetail', [
      'fetchCheckin', 'unsetEventPopup'
    ]),
    ...mapActions('bannerGetPica', [
      'setCheckinSettings',
      'exitCheckin'
    ]),

    async loadCheckinDetailData() {
      if (!this.$store.state.appConfig.hideCart)
        await this.$store.dispatch('cart/getCart')
      await this.getCheckinDetail(this.$route.params.picacode)


      this.subscribeToWebSocketCheckinEvents(this.$route.params.picacode)
      this.checkinEventsHandler = (message) => {
        if (message.type === 'checkin.new_medias_found' && message.data.picacode === this.$route.params.picacode) {
          this.getCheckinDetail(this.$route.params.picacode)
          if (this.$refs.checkinDetailPhotosList) this.$refs.checkinDetailPhotosList.loadMedias()
        }
      }
      this.setCheckinSettings(this.useGetPicaForYourEvent)
      await this.$store.dispatch('websocket/subscribeOnMessageHandler', this.checkinEventsHandler)
    },
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async getCheckinDetail(picacode) {
      await this.delay(this.$route.params.msDelay || 0)
      try {
        await this.fetchCheckin(picacode)
      } catch (error) {
        if (error.response.status === 404) {
          this.$bvToast.toast(this.$tc('notifications.checkin_not_found', picacode), {variant: 'warning'})
          await this.$router.push({name: 'checkins-list'})
          return
        }
      }

      this.SET_PICASERVICE({picaService: this.checkin.pica_service})

      // hard-coded toast for convivio event
      this.showBottomToastMessageIfNeeded()
      this.showCustomModalMessages()

      this.priceForAllPhotos = this.getPriceByQty(-1)
      this.priceForAllPhotosAndVideos = this.getPriceByQty(-2)
      this.priceOriginalForAllPhotos = this.getPriceByQty(-1, true)
      this.priceOriginalForAllPhotosAndVideos = this.getPriceByQty(-2, true)
      const eventEnd = new Date(this.checkin.pica_service.event_end_at)

      const unlockedVideosWithoutPhotosAfterEventCond = !this.hasBuyablePhotos && !this.hasBuyableVideos && !this.checkin.has_bought_photos && this.checkin.has_bought_videos //&& new Date() > eventEnd
      if ((this.isPhotoPackageBannerEnabled || this.isVideoPackageBannerEnabled) && !unlockedVideosWithoutPhotosAfterEventCond) {
        this.currentTab = BUYABLE
      } else {
        this.currentTab = BOUGHT
      }

      // open face recognition modal
      if (this.$route.params.openFaceReconition) {
        this.openFaceRecognitionModal(true);
      }

      this.loading = false
      await this.$nextTick()
      await observeElement(this.$el.querySelector(".button-banners-wrap"), this.timeoutObserver)
      Gallery.enterInAlbum(this.picaServiceId)
      this.timerStarted = true
    },


    getPriceByQty(qty, originalPrice = false, formatted = true) {
      if (this.isPromo) {
        return (0).toLocaleString(this.locale, {
          style: 'currency',
          currency: this.checkin.pica_service.currency,
          currencyDisplay: 'symbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
      }
      // this.checkin.price_list will be null when the event is promo
      const item = this.checkin.price_list.items.find(item => item.quantity === qty)
      if (formatted) {
        return item ? (originalPrice ? item.price_original_formatted : item.price_formatted) : null
      } else {
        return item ? (originalPrice ? item.price_original : item.price) : null
      }

    },
    async activatePackage(orderType = 'photopackage') {
      return new Promise((resolve, reject) => {

        if (this.digitalItemId) {
          this.$store.dispatch(
              'cart/setDigitalItemAsPackage',
              {digitalItemId: this.digitalItemId, orderType}
          ).then(response => {
            // TODO: change state of all photos
            // this.setPhotoItemLoadingState(photo.id, false)


            resolve(response)
            this.$router.push({name: 'checkout'})
          }).catch(error => {
            reject(error)
          })
        } else {
          this.$store.dispatch(
              'cart/addDigitalItemToCartAsPackage',
              {picacode: this.checkin.picacode, orderType}
          ).then(response => {
            this.digitalItemId = response.data.id
            // TODO: change state of all photos
            // this.setPhotoItemLoadingState(photo.id, false)
            resolve(response)
            this.$router.push({name: 'checkout'})
          }).catch(error => {
            if (error.response.status === 400 && error.response.data.code === 'cart_currency_mismatch') {
              this.$bvModal.show('modal-currency-mismatch')
            }
            if (error.response.status === 400 && error.response.data.code === 'purchase_disabled') {
              this.$bvToast.toast(error.response.data.detail, {
                title: this.$t('collage_photo_item.purchase_not_available'),
                headerClass: 'd-block pt-2',
                autoHideDelay: 5000,
                toaster: 'b-toaster-top-center'
              })
            }

            reject(error)
          })
        }
      })

    },
    activateCustomPackage(endpointUrl) {
      this.$store.dispatch(
          'cart/addCustomPackage',
          {endpointUrl: endpointUrl, checkinId: this.checkin.checkin_id}
      ).then(response => {
        this.digitalItemId = response.data.id
        // TODO: change state of all photos
        // this.setPhotoItemLoadingState(photo.id, false)
        this.$router.push({name: 'checkout'})
      }).catch(error => {
        if (error.response.status === 400 && error.response.data.code === 'cart_currency_mismatch') {
          this.$bvModal.show('modal-currency-mismatch')
        } else if (error.response.status === 400 && error.response.data.code === 'purchase_disabled') {
          this.$bvToast.toast(error.response.data.detail, {
            title: this.$t('collage_photo_item.purchase_not_available'),
            headerClass: 'd-block pt-2',
            autoHideDelay: 5000,
            toaster: 'b-toaster-top-center'
          })
        }
      })
    },
    subscribeToWebSocketCheckinEvents(picacode) {
      this.$store.dispatch('websocket/sendMessage',
          {'type': 'checkin.subscribe', 'data': {'picacodes': [picacode]}}
      )
    },
    openFaceRecognitionModal(recognitionFallbackMode = false) {
      this.openAddSelfieStep({
        checkin: {id: this.checkin.checkin_id, picacode: this.checkin.picacode},
        max_faces_count: this.checkin.max_faces_count,
        checkin_faces: this.checkin.checkin_faces,
        user_cta: this.checkin.user_cta,
        liveness_detection_enabled: this.checkin.liveness_detection_enabled,
        stay_mode: null,
        face_checkin_privacy_statement: null,
        callback: (checkinId, newCheckinFaces, faceCheckinComplete = false) => {
          if (checkinId === this.checkin.checkin_id) {
            this.$set(this.checkin, 'checkin_faces', newCheckinFaces)
          }

          if (recognitionFallbackMode && faceCheckinComplete) {
            this.showUploadingMessage = true
          }

        }
      })
    },
    downloadAlbum() {
      EventService.downloadAlbum(this.checkin.checkin_id)
          .then(response => {
            this.$bvToast.toast(
                this.$t('toast_download_album.text'),
                {
                  title: this.$t('toast_download_album.title'),
                  headerClass: 'd-block pt-2',
                  noCloseButton: false,
                  autoHideDelay: 5000
                })
          })
          .catch(error => {
            if (error.response.status === 400 && error.response.data.code === 'bulk_download_not_available') {
              this.$bvToast.toast(
                  error.response.data.detail,
                  {variant: 'warning', autoHideDelay: 5000})
            }
          })
    },
    showBottomToastMessageIfNeeded() {
      // hard-coded toast for convivio event
      if (this.picaServiceId !== 1504) return

      setTimeout(() => {
        const extraCss = this.$parent.showCheckoutBanner ? 'has-checkout-banner' : ''
        const h = this.$createElement

        // Create the message
        const vNodesMsg = h(
            'p',
            {class: ['text-left', 'mb-0']},
            [this.$t('toast_checkin_detail.personal_use_line_1'), h('br'), this.$t('toast_checkin_detail.personal_use_line_2')]
        )
        this.$bvToast.toast(vNodesMsg,
            {
              autoHideDelay: 200000,
              headerClass: '',
              toaster: 'b-toaster-bottom-center b-toaster-bottom-center__checkin-detail ' + extraCss,
              noCloseButton: false,
            });
      }, 200)
    },
    onAnonymizeCallBack({anonymizeStatus, checkinFaceMissing}) {
      this.checkin.anonymize_status = anonymizeStatus
      if (checkinFaceMissing) {
        this.openFaceRecognitionModal();
      }
    },
    showCustomModalMessages() {
      if (this.getCustomModalCheckinDetailShown(this.picaServiceId)) return

      if (this.picaServiceId === 2178)
        this.modalCustom.text = this.$t('modal_custom_checkin_detail.text_event_2178')
      else if (this.picaServiceId === 2440 || this.picaServiceId === 2468 || this.picaServiceId === 2556)
        this.modalCustom.text = this.$t('modal_custom_checkin_detail.text_event_2440', {privacy_file: 'https://www.inter.it/it/informativa-privacy-serviziopica'}) // inter
      else if (this.picaServiceId === 2363) {
        this.modalCustom.config = {scroll: false, textClass: 'text-primary'}
        this.modalCustom.text = this.$t('modal_custom_checkin_detail.text_event_2363') // mgmotor
      } else if (this.picaServiceId === 2394) {
        this.modalCustom.ctas.push({
          label: "Go to UW vs. Oregon album",
          url: "https://www.getpica.com/preview/?ps=2295&pc=%23ALASKA23-C843680DE-7BJZ4XHP4&src=dl&tag=calendar"
        })
        this.modalCustom.title = this.$t('modal_custom_checkin_detail.title_event_2394')
        this.modalCustom.text = this.$t('modal_custom_checkin_detail.text_event_2394')
        this.modalCustom.config = {scroll: false, textCenter: true}
      } else if (this.picaServiceId === 2491) {
        this.modalCustom.ctas.push({
          label: "Go to The Global Event Tech Summit 2023",
          url: "https://www.getpica.com/preview/?ps=2385&pc=%23CNSUMMIT-DC89958EC-QW77ROEP6&src=dl&tag=calendar"
        }, {
          label: "Go to EN Indy Conference and Awards 2023",
          url: "https://www.getpica.com/preview/?ps=2193&pc=%23ENINDY23-F5BD25056-AASJVGB8E&src=dl&tag=calendar"
        })
        this.modalCustom.title = this.$t('modal_custom_checkin_detail.title_event_2491')
        this.modalCustom.text = this.$t('modal_custom_checkin_detail.text_event_2491')
        this.modalCustom.config = {scroll: false, textCenter: true}
      } else if (this.picaServiceId === 2511) {
        this.modalCustom.btnOk = this.$t('modal_custom_checkin_detail.btn_ok_event_2511')
        this.modalCustom.text = this.$t('modal_custom_checkin_detail.text_event_2511')
        this.modalCustom.config = {scroll: false, textCenter: true}
      } else {
        return
      }

      setTimeout(() => this.$bvModal.show('modal-custom-checkin-detail'), 600)
      this.$store.commit('SET_CUSTOM_MODAL_CHECKIN_DETAIL_SHOWN', {[this.picaServiceId]: true})
    }
  },
}
</script>

